<script lang="ts">
import { Popup, setWorkerUrl } from "maplibre-gl";
import type { IControl, Map } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
// Vite's dependency optimizer serves MapLibre from node_modules/.vite/deps,
// where MapLibre's sibling-worker fallback points at a worker file Vite does
// not emit. Use MapLibre's documented Vite pattern so Vite owns the worker URL.
import maplibreWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import { localizeStyle } from "@americana/diplomat";
import { toast } from "svelte-sonner";
import {
  AttributionControl,
  CustomControl,
  MapLibre,
  NavigationControl,
} from "svelte-maplibre-gl";
import MaplibreInspect from "@maplibre/maplibre-gl-inspect";
import "@maplibre/maplibre-gl-inspect/dist/maplibre-gl-inspect.css";
import { runtimeConfig } from "../../config.ts";
import { PoiDensityController } from "../../lib/geo/poi-density/index.ts";
import type { LngLat } from "../../lib/geo/types.ts";
import { userPreferences } from "../../lib/preferences.svelte.ts";
import { fetchIpInfoLocation } from "../../lib/ipGeolocation.ts";
import type { PoiSearchCamera } from "../../lib/search/area.ts";
import type { PoiCategory } from "../../lib/search/categories.ts";
import {
  createPoiSearchController,
  type PoiSearchState,
} from "../../lib/search/controller.ts";
import { DEFAULT_STYLE_ID, STYLES } from "../../styles.ts";
import * as ContextMenu from "../ui/context-menu/index.ts";
import { Button } from "../ui/button/index.ts";
import { Spinner } from "../ui/spinner/index.ts";
import SelectionPanel from "../SelectionPanel";
import {
  type ExternalAppLink,
  externalAppLinks,
} from "../../lib/externalApps.ts";
import Search from "../Search/Search.svelte";
import PassiveGeolocationControl from "../PassiveGeolocationControl.svelte";
import StyleSwitcherControl from "../StyleSwitcher";
import {
  attachPoiSearchResultsPresentation,
  type PoiSearchResultsPresentation,
} from "./map/poiSearchResults.ts";
import { useMapSelection } from "./selection/useMapSelection.svelte.ts";
import type { OsmSelectionTarget, OsmState } from "./selection/osmLookup.ts";

// Must run before the first map is created.
setWorkerUrl(maplibreWorkerUrl);

type SavedPosition = { center: LngLat; zoom: number };
type BrowserGeolocationStatus = "checking" | "active" | "inactive";

const POSITION_KEY = "map-position";
const DEFAULT_VIEW = {
  center: [0, 20] as LngLat,
  zoom: 2,
};

function loadPosition(): SavedPosition | null {
  try {
    const raw = localStorage.getItem(POSITION_KEY);
    return raw ? (JSON.parse(raw) as SavedPosition) : null;
  } catch {
    return null;
  }
}

function savePosition(currentMap: Map | null | undefined): void {
  if (!currentMap) return;
  const { lng, lat } = currentMap.getCenter();
  const position: SavedPosition = {
    center: [lng, lat],
    zoom: currentMap.getZoom(),
  };
  localStorage.setItem(POSITION_KEY, JSON.stringify(position));
}

function currentPoiSearchCamera(currentMap: Map): PoiSearchCamera {
  const currentCenter = currentMap.getCenter();
  const center: LngLat = [currentCenter.lng, currentCenter.lat];
  const bounds = currentMap.getBounds();

  return {
    center,
    zoom: currentMap.getZoom(),
    visibleBounds: {
      west: bounds.getWest(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      north: bounds.getNorth(),
    },
  };
}

function updatePoiSearchCamera(): void {
  if (!map || poiSearch.status === "idle") return;
  poiSearchController.updateCamera(currentPoiSearchCamera(map));
}

function selectGeocoderResult(selectionTarget: OsmSelectionTarget): void {
  poiSearchController.reset();
  // Geocoder selections are unrelated to the previously clicked POI result, so
  // they should not preserve that result's temporary hidden state while
  // loading.
  poiResultsPresentation?.clearActiveSelection();
  void selection.selectFromExternalTarget(selectionTarget);
}

function selectPoiCategory(category: PoiCategory): void {
  if (!map) return;
  poiSearchController.search(category, currentPoiSearchCamera(map));
}

function clearSearch(): void {
  poiSearchController.reset();
}

function openExternalApp(link: ExternalAppLink): void {
  window.open(link.href, "_blank", "noopener,noreferrer");
}

function applyActiveResultSelection(
  presentation: PoiSearchResultsPresentation | undefined,
  osm: OsmState,
): void {
  // POI-result clicks optimistically hide their marker before exact lookup
  // starts. Preserve that hide through the loading state; other interaction
  // sources clear stale hides before they trigger loading.
  if (osm.status === "loading") return;
  if (osm.status === "success") {
    presentation?.setActiveSelection({
      type: osm.active.type,
      id: osm.active.id,
    });
    return;
  }
  presentation?.clearActiveSelection();
}

const savedPosition = loadPosition();
const defaultStyle = STYLES.find((s) => s.id === DEFAULT_STYLE_ID)!;
const currentStyle = $derived(
  STYLES.find((s) => s.id === userPreferences.current.mapStyleId) ??
    defaultStyle,
);

let map = $state<Map | undefined>(undefined);
let poiDensityController = $state<PoiDensityController | undefined>(
  undefined,
);
let poiResultsPresentation = $state<PoiSearchResultsPresentation | undefined>(
  undefined,
);
// Diplomat calls setStyle(), which emits styledata again. Track the style URL
// to avoid relocalizing that event while still localizing newly selected styles.
let localizedStyleUrl: string | undefined;
let center = $state(savedPosition?.center ?? DEFAULT_VIEW.center);
let zoom = $state(savedPosition?.zoom ?? DEFAULT_VIEW.zoom);
let bearing = $state(0);
let pitch = $state(0);
let roll = $state(0);
let userLngLat = $state<LngLat | undefined>(undefined);
let browserGeolocationStatus = $state<BrowserGeolocationStatus>("checking");
let occupiedDesktopWidth = $state(0);
let contextMenuTrigger = $state<HTMLElement | null>(null);
// Active POI category search belongs at the map level so it can outlive the
// transient search input and later drive result pins plus "search in this area".
let poiSearch = $state<PoiSearchState>({ status: "idle" });
const photonApiUrl = runtimeConfig.photonApiUrl;
const poiSearchController = createPoiSearchController((next) => {
  const previousStatus = poiSearch.status;
  poiSearch = next;
  // Camera movement can re-emit an existing empty state with a new stale value.
  // Only a completed request should produce an empty-results toast.
  if (previousStatus === "loading" && next.status === "empty") {
    toast.info("No results found in this area.", {
      id: "empty-poi-search",
      closeButton: true,
      dismissible: true,
    });
  }
  if (
    previousStatus === "loading" && next.status === "success" &&
    next.truncated
  ) {
    toast.info(
      `Showing first ${next.results.length} results, zoom in to see more.`,
      {
        id: "truncated-poi-search",
        closeButton: true,
        dismissible: true,
        duration: 2500,
      },
    );
  }
});
const selection = useMapSelection({
  map: () => map,
  occupiedDesktopWidth: () => occupiedDesktopWidth,
  contextMenuTrigger: () => contextMenuTrigger,
  onMapSelectionStart: () => poiResultsPresentation?.clearActiveSelection(),
});
const styleSwitcherTransform = $derived(
  occupiedDesktopWidth > 0
    ? `translateX(${occupiedDesktopWidth}px)`
    : undefined,
);
const showSearchThisArea = $derived(
  map !== undefined &&
    ((poiSearch.status === "loading" && poiSearch.source === "area") ||
      (poiSearch.status !== "idle" && poiSearch.status !== "loading" &&
        poiSearch.stale)),
);

function localizeLabels(): void {
  if (!map || localizedStyleUrl === currentStyle.styleUrl) return;

  localizedStyleUrl = currentStyle.styleUrl;
  localizeStyle(map as unknown as Parameters<typeof localizeStyle>[0]);
}

const inspectControl = new MaplibreInspect({
  popup: new Popup({
    closeButton: false,
    closeOnClick: false,
  }) as unknown as ConstructorParameters<typeof MaplibreInspect>[0]["popup"],
}) as unknown as IControl;

$effect(() => {
  if (!map) return;

  const controller = new PoiDensityController(map);
  let resultsPresentation: PoiSearchResultsPresentation | undefined;
  resultsPresentation = attachPoiSearchResultsPresentation(map, {
    onSelect: (selectionTarget) => {
      void selection.selectFromExternalTarget(selectionTarget);
    },
  });
  poiDensityController = controller;
  poiResultsPresentation = resultsPresentation;

  return () => {
    controller.destroy();
    resultsPresentation.destroy();
    poiDensityController = undefined;
    poiResultsPresentation = undefined;
  };
});

$effect(() => {
  if (!map || savedPosition || browserGeolocationStatus !== "inactive") {
    return;
  }

  const abortController = new AbortController();
  void (async () => {
    try {
      const { lngLat, zoom } = await fetchIpInfoLocation(
        runtimeConfig.ipInfoToken,
        abortController.signal,
      );
      if (abortController.signal.aborted) return;
      map?.jumpTo({ center: lngLat, zoom });
    } catch (error: unknown) {
      if (!abortController.signal.aborted) {
        console.error("IP geolocation failed", error);
      }
    }
  })();

  return () => abortController.abort();
});

$effect(() => {
  poiResultsPresentation?.setSearchState(poiSearch);
});

$effect(() => {
  applyActiveResultSelection(poiResultsPresentation, selection.osm);
});

$effect(() => {
  updatePoiSearchCamera();
});

$effect(() => {
  return () => {
    poiSearchController.cancel();
  };
});

$effect(() => {
  if (!map) return;
  if (!poiDensityController) {
    console.warn("POI density controller is unavailable after map creation.");
    return;
  }

  poiDensityController.update(
    currentStyle.id,
    userPreferences.current.poiDensity ?? "default",
    currentStyle.poiDensityAdapter,
  );
});

$effect(() => {
  if (!map) return;

  const canvas = map.getCanvas();
  const shouldDimMap = (poiSearch.status === "success" &&
    poiSearch.results.length > 0) || poiSearch.status === "loading";
  canvas.style.transition = "filter 500ms ease-out";
  canvas.style.filter = shouldDimMap ? "saturate(0.5)" : "";

  return () => {
    canvas.style.filter = "";
    canvas.style.transition = "";
  };
});
</script>

<div class="fixed inset-0 h-full w-full overflow-hidden bg-[#f3f1eb]">
  <ContextMenu.Root>
    <MapLibre
      class="h-full w-full"
      autoloadGlobalCss={false}
      style={currentStyle.styleUrl}
      bind:map
      bind:center
      bind:zoom
      bind:bearing
      bind:pitch
      bind:roll
      attributionControl={false}
      onstyledata={localizeLabels}
      onmoveend={() => {
        savePosition(map);
        updatePoiSearchCamera();
      }}
    >
      {#if bearing !== 0 || pitch !== 0 || roll !== 0}
        <NavigationControl
          position="top-right"
          showZoom={false}
          visualizePitch
          visualizeRoll
        />
      {/if}
      <PassiveGeolocationControl
        {map}
        onlocationchange={(lngLat) => userLngLat = lngLat}
        ongeolocationstatuschange={(status) => {
          browserGeolocationStatus = status;
        }}
      />
      <CustomControl position="top-right" control={inspectControl} />
      {#if map}
        <CustomControl position="top-left" group={false}>
          <Search
            {map}
            {photonApiUrl}
            {userLngLat}
            ongeocoderselect={selectGeocoderResult}
            oncategoryselect={selectPoiCategory}
            onclear={clearSearch}
            {poiSearch}
          />
        </CustomControl>
      {/if}
      {#if map}
        <CustomControl position="bottom-left" group={false}>
          <div
            class="transition-transform"
            style:transform={styleSwitcherTransform}
          >
            <StyleSwitcherControl
              {map}
              styleUrl={currentStyle.styleUrl}
              poiDensity={userPreferences.current.poiDensity ?? "default"}
              onstylechange={(style) => userPreferences.current.mapStyleId = style.id}
              onpoidensitychange={(density) => userPreferences.current.poiDensity = density}
            />
          </div>
        </CustomControl>
      {/if}
      <AttributionControl position="bottom-right" compact />
    </MapLibre>

    <!-- Bits UI owns menu behavior, but MapLibre owns gesture detection. Keep
         this trigger outside MapLibre so controls cannot reach its built-in
         long-press handler; mapSelectionGestures opens it programmatically to
         reuse Bits UI's positioning, focus, and dismissal behavior. -->
    <ContextMenu.Trigger
      bind:ref={contextMenuTrigger}
      class="pointer-events-none absolute size-0"
      aria-hidden="true"
    />

    <ContextMenu.Content>
      <ContextMenu.Group>
        <ContextMenu.Item
          disabled={!selection.canLookupContextMenuTarget}
          onSelect={selection.showContextMenuLookup}
        >
          What's here?
        </ContextMenu.Item>
        {#if selection.contextMenuLngLat && map}
          <ContextMenu.Sub>
            <ContextMenu.SubTrigger>Open in…</ContextMenu.SubTrigger>
            <ContextMenu.SubContent>
              <ContextMenu.Group>
                {#each externalAppLinks({
                  kind: "coordinates",
                  lngLat: selection.contextMenuLngLat,
                  zoom: map.getZoom(),
                }) as link}
                  <ContextMenu.Item
                    onSelect={() => openExternalApp(link)}
                  >
                    {link.label}
                  </ContextMenu.Item>
                {/each}
              </ContextMenu.Group>
            </ContextMenu.SubContent>
          </ContextMenu.Sub>
        {/if}
      </ContextMenu.Group>
    </ContextMenu.Content>
  </ContextMenu.Root>

  <!-- Keep app overlays outside .maplibregl-map so MapLibre's global CSS does not affect them. -->
  <div class="contents">
    {#if showSearchThisArea && map}
      <div
        class="fixed top-4 left-1/2 z-[4] -translate-x-1/2"
      >
        <Button
          type="button"
          size="sm"
          variant="outline"
          class="rounded-full cursor-pointer"
          disabled={poiSearch.status === "loading" && poiSearch.source === "area"}
          aria-busy={poiSearch.status === "loading" && poiSearch.source === "area"}
          onclick={() =>
            poiSearchController.refresh(currentPoiSearchCamera(map!))}
        >
          {#if poiSearch.status === "loading" && poiSearch.source === "area"}
            <Spinner
              data-icon="inline-start"
              role="presentation"
              aria-hidden="true"
            />
          {/if}
          Search this area
        </Button>
      </div>
    {/if}
    {#if selection.osm.status !== "idle"}
      <SelectionPanel
        osm={selection.osm}
        bind:occupiedDesktopWidth={occupiedDesktopWidth}
        onclose={selection.clearSelection}
        onselect={selection.selectOsmElement}
      />
    {/if}
  </div>
</div>
